import { useState, useEffect, useCallback } from 'react';
import type { Participant } from '../types';

interface PopoutMeetingState {
  participants: Participant[];
  localParticipant: Participant | null;
  screenShareOwnerId: string | null;
  screenShareTrack: MediaStreamTrack | null;
  isConnected: boolean;
  isSynced: boolean;
}

interface ParentMeetingStore {
  getState: () => {
    participants: Participant[];
    localParticipant: Participant | null;
    screenShareOwnerId: string | null;
    screenShareTrack: MediaStreamTrack | null;
    isConnected: boolean;
  };
}

interface ParentWindow extends Window {
  __MEETING_STORE__?: ParentMeetingStore;
}

const POLL_INTERVAL = 100;

/**
 * Accesses meeting state from parent window for pop-out pages.
 */
export function usePopoutMeetingState(): PopoutMeetingState {
  const [state, setState] = useState<PopoutMeetingState>({
    participants: [],
    localParticipant: null,
    screenShareOwnerId: null,
    screenShareTrack: null,
    isConnected: false,
    isSynced: false,
  });

  const syncFromParent = useCallback(() => {
    const parentWindow = window.opener as ParentWindow | null;

    if (!parentWindow) {
      return false;
    }

    try {
      const store = parentWindow.__MEETING_STORE__;
      if (!store) {
        return false;
      }

      const parentState = store.getState();
      setState({
        participants: parentState.participants,
        localParticipant: parentState.localParticipant,
        screenShareOwnerId: parentState.screenShareOwnerId,
        screenShareTrack: parentState.screenShareTrack,
        isConnected: parentState.isConnected,
        isSynced: true,
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const synced = syncFromParent();
      if (!synced && state.isSynced) {
        setState((prev) => ({ ...prev, isSynced: false }));
      }
    }, POLL_INTERVAL);

    syncFromParent();

    return () => clearInterval(interval);
  }, [syncFromParent, state.isSynced]);

  return state;
}
